import type { ReactNode } from 'react';
import { Link, useNavigate, useSearchParams } from 'react-router';
import {
  NO_VALUE_KEY,
  addDays,
  addMonths,
  byCategory,
  byDayOfWeek,
  byMethod,
  bySource,
  detectRecurring,
  monthRange,
  monthStart,
  monthlyTotals,
  periodChange,
  previousRange,
  rangeLength,
  shiftPreset,
  summarizeSpending,
  timeGrain,
  timeSeries,
  topNotes,
  type DateRange,
  type SpendDimension,
} from '@tahi/core';
import { HBarList } from '../../../ui/charts';
import { Money, ScreenHeader } from '../../../ui';
import { ColumnSection } from '../components/ColumnSection';
import { RangePicker } from '../components/RangePicker';
import { copy, SPENDING_BACK } from '../copy';
import { drillHref, parseRangeParams, rangeSearch } from '../drill';
import {
  WEEKDAY_SHORT,
  bucketLabel,
  categoryColor,
  categoryName,
  changeText,
  methodLabel,
  monthShort,
  shortDay,
  sourceLabel,
  spokenPeso,
  timeLabel,
} from '../format';
import { useCategories, useEntries, useToday, useWeekStart } from '../hooks';
import { CARD, FOCUS, LABEL, SCREEN } from '../styles';

function Section({ title, children }: { title: string; children: ReactNode }) {
  return (
    <section aria-label={title} className={CARD}>
      <h2 className={`mb-2 ${LABEL}`}>{title}</h2>
      {children}
    </section>
  );
}

const series = (i: number) => `var(--chart-${(i % 6) + 1})`;

export function BreakdownScreen() {
  const [sp, setSp] = useSearchParams();
  const navigate = useNavigate();
  const today = useToday();
  const ws = useWeekStart();
  const { preset, range } = parseRangeParams(sp, today, ws);
  const previous = preset === 'custom' ? previousRange(range) : shiftPreset(preset, range, -1, ws);
  const recurringStart = addDays(range.end, -179);
  const sixMonthsStart = addMonths(monthStart(range.end), -6); // one extra month for the first change
  const loadStart = [previous.start, recurringStart, sixMonthsStart].sort()[0] ?? range.start;
  const entries = useEntries({ start: loadStart, end: range.end });
  const categories = useCategories() ?? [];

  if (entries === undefined) {
    return (
      <main className={SCREEN}>
        <p className="text-muted">{copy.loading}</p>
      </main>
    );
  }

  const byId = new Map(categories.map((c) => [c.id, c]));
  const order = categories.map((c) => c.id);
  const labelCtx = { categoryName: (id: string) => categoryName(id, byId) };
  const opts = { weekStartsOn: ws };
  const href = (dim: SpendDimension, key: string, r: DateRange = range) =>
    drillHref({ range: r, dim, key, weekStartsOn: ws });
  const go = (dim: SpendDimension) => (key: string) => navigate(href(dim, key));

  const summary = summarizeSpending(entries, range);
  const change = changeText(periodChange(entries, range, previous), preset);
  const grain = timeGrain(range);
  const days = rangeLength(range);
  const notes = topNotes(entries, range, 5);
  const recurring = detectRecurring(entries.filter((e) => e.date >= recurringStart));
  const months = monthlyTotals(entries, range.end.slice(0, 7), 6);

  return (
    <main className={SCREEN}>
      <ScreenHeader title={copy.breakdownTitle} back={SPENDING_BACK} />
      <RangePicker
        preset={preset}
        range={range}
        today={today}
        weekStartsOn={ws}
        onChange={(p, r) =>
          setSp(new URLSearchParams(rangeSearch(p, r).slice(1)), { replace: true })
        }
      />

      <section aria-label={copy.totalLabel} className={CARD}>
        <button
          type="button"
          aria-label={`${copy.totalLabel}, ${spokenPeso(summary.totalMinor)}, ${copy.entriesCount(summary.count)}`}
          onClick={() => navigate(drillHref({ range, weekStartsOn: ws }))}
          className={`block min-h-11 w-full rounded-xl text-left ${FOCUS}`}
        >
          <Money minor={summary.totalMinor} className="text-[40px] leading-tight" />
          <span className="block text-sm text-muted">{copy.entriesCount(summary.count)}</span>
        </button>
        {change ? <p className="mt-1 text-sm text-muted">{change}</p> : null}
      </section>

      {summary.count === 0 ? (
        <p className="text-muted">{copy.noSpendingInRange}</p>
      ) : (
        <>
          <Section title={copy.byCategory}>
            <HBarList
              ariaLabel={copy.byCategory}
              onSelect={go('category')}
              items={byCategory(entries, range).map((b) => ({
                key: b.key,
                label: categoryName(b.key, byId),
                valueMinor: b.totalMinor,
                color: categoryColor(b.key, order),
              }))}
            />
          </Section>

          <ColumnSection
            title={copy.overTime}
            items={timeSeries(entries, range, grain, opts).map((b) => ({
              key: b.key,
              label: timeLabel(grain, b.key, days),
              valueMinor: b.totalMinor,
            }))}
            captionFor={(k) => bucketLabel(grain, k, labelCtx)}
            drillFor={(k) => href(grain, k)}
          />

          <ColumnSection
            title={copy.dayOfWeek}
            color="var(--chart-2)"
            items={byDayOfWeek(entries, range, opts).map((b) => ({
              key: b.key,
              label: WEEKDAY_SHORT[Number(b.key)] ?? b.key,
              valueMinor: b.totalMinor,
            }))}
            captionFor={(k) => bucketLabel('weekday', k, labelCtx)}
            drillFor={(k) => href('weekday', k)}
          />

          <Section title={copy.bySource}>
            <HBarList
              ariaLabel={copy.bySource}
              onSelect={go('source')}
              items={bySource(entries, range).map((b, i) => ({
                key: b.key,
                label: sourceLabel(b.key),
                valueMinor: b.totalMinor,
                color: series(i),
              }))}
            />
          </Section>

          <Section title={copy.byMethod}>
            <HBarList
              ariaLabel={copy.byMethod}
              onSelect={go('method')}
              items={byMethod(entries, range).map((b, i) => ({
                key: b.key,
                label: methodLabel(b.key),
                valueMinor: b.totalMinor,
                color: b.key === NO_VALUE_KEY ? 'var(--chart-none)' : series(i),
              }))}
            />
          </Section>

          {notes.length > 0 ? (
            <Section title={copy.topNotes}>
              <HBarList
                ariaLabel={copy.topNotes}
                onSelect={go('note')}
                items={notes.map((b) => ({
                  key: b.key,
                  label: b.label ?? b.key,
                  valueMinor: b.totalMinor,
                  color: 'var(--chart-3)',
                }))}
              />
            </Section>
          ) : null}

          {recurring.length > 0 ? (
            <Section title={copy.repeats}>
              <ul className="divide-y divide-divider">
                {recurring.map((r) => (
                  <li key={r.key}>
                    <Link
                      to={href('note', r.key, { start: recurringStart, end: range.end })}
                      className={`flex min-h-11 items-center justify-between gap-3 rounded-xl px-2 py-2 ${FOCUS}`}
                    >
                      <span className="min-w-0">
                        <span className="block truncate text-fg">{r.label}</span>
                        <span className="text-xs text-muted">
                          {copy.cadence[r.cadence]} · {copy.nextAround(shortDay(r.nextExpected))}
                        </span>
                      </span>
                      <Money minor={r.typicalAmountMinor} />
                    </Link>
                  </li>
                ))}
              </ul>
            </Section>
          ) : null}
        </>
      )}

      <ColumnSection
        title={copy.lastSixMonths}
        color="var(--chart-3)"
        items={months.map((m) => ({
          key: m.month,
          label: monthShort(m.month),
          valueMinor: m.totalMinor,
        }))}
        captionFor={(k) => bucketLabel('month', k, labelCtx)}
        drillFor={(k) => href('month', k, monthRange(k))}
      />
    </main>
  );
}
